import React, { useState } from 'react';

import { AlertAnimation } from '@eika/alert-animation';
import { ErrorMessage } from '@eika/attention-message';
import { Button } from '@eika/button';
import { WarningCard } from '@eika/card';
import { Combobox } from '@eika/combobox';
import { useViewType, ViewType } from '@eika/hooks';

import { useBankCountries } from '../../../../fetchHooks/useCountryDetails';
import useHandleCrossBorderReload from '../../../../hooks/useHandleCrossBorderReload';
import useTranslation from '../../../../hooks/useTranslation';
import CenteredModalContent from '../../../shared/CenteredModalContent/CenteredModalContent';
import { NavigationButtons } from '../../../shared/NavigationButtons';
import { CrossBorderPaymentActionType } from '../form/actions';
import useFormContext from '../form/useFormContext';
import { receiverError, validateNewReceiver } from '../form/validate';
import NewCrossBorderReceiverBank from './NewCrossBorderReceiverBank';
import NewCrossBorderReceiverSkeleton from './NewCrossBorderReceiverSkeleton';

import './NewCrossBorderReceiver.scss';

const NewCrossBorderReceiver: React.FC = () => {
    const translated = useTranslation();
    const viewType = useViewType();
    const {
        data: countryList,
        error: countryListError,
        isLoading: loadingCountries,
        refetch: refreshCountries,
    } = useBankCountries();
    const { handleChange, state } = useFormContext();
    const [showNextButton, setShowNextButton] = useState(true);
    const [chosenCountry, setChosenCountry] = useState(state.country?.name || '');
    const [validationErrors, setValidationErrors] = useState<receiverError>();
    useHandleCrossBorderReload();

    const validateFields = () => {
        const validationErrors = validateNewReceiver(translated, state);
        setValidationErrors(validationErrors);
        return !(
            validationErrors.receiverName ||
            validationErrors.receiverCountryCode ||
            validationErrors.bankCountryCode ||
            validationErrors.bic ||
            validationErrors.accountNumber ||
            validationErrors.creditorDetails ||
            validationErrors.nationalId
        );
    };

    const handleCountrySelection = (value: string) => {
        setValidationErrors({ bankCountryCode: undefined });
        const country = countryList?.find((country) => country.name.toLowerCase() === value.toLowerCase());
        handleChange({
            type: CrossBorderPaymentActionType.SET_BANK_COUNTRY,
            payload: country?.countryCode,
        });
        setChosenCountry(country?.name ?? value);
    };

    if (countryListError) {
        return (
            <CenteredModalContent>
                <WarningCard
                    className="cross-border-payment__country-warning-card"
                    title={translated.crossBorderPayment.newReceiver.errorCountryTitle}
                    isModal
                    icon={<AlertAnimation />}
                >
                    <p>{translated.crossBorderPayment.newReceiver.errorCountry}</p>
                    <Button className="cross-border-payment__country-warning-retry" onClick={() => refreshCountries()}>
                        {translated.tryAgain}
                    </Button>
                </WarningCard>
            </CenteredModalContent>
        );
    }

    const otherError = validationErrors?.accountNumber ?? validationErrors?.nationalId ?? validationErrors?.bic;
    return (
        <>
            <h2 className="new-receiver-title">{translated.crossBorderPayment.newReceiver.title}</h2>
            <h3 className="new-receiver-sub-title">{translated.crossBorderPayment.newReceiver.subTitle}</h3>
            {loadingCountries && <NewCrossBorderReceiverSkeleton />}
            {!loadingCountries && countryList && (
                <div className="cross-border-payment__country-search max-desktop-width">
                    <Combobox
                        id="new-receiver-country-select"
                        label={translated.crossBorderPayment.newReceiver.bankCountry}
                        suggestions={countryList?.map((country) => {
                            return { value: country.name };
                        })}
                        value={chosenCountry}
                        onChange={(value) => {
                            handleCountrySelection(value);
                        }}
                        onSelect={(value) => handleCountrySelection(value.toString())}
                        error={validationErrors?.bankCountryCode}
                        data-testid="select-new-receiver-country-input"
                    />
                </div>
            )}
            <NewCrossBorderReceiverBank
                countryCode={state.receiver?.bankCountryCode}
                validationErrors={validationErrors}
                setValidationErrors={setValidationErrors}
                setShowNextButton={setShowNextButton}
            />
            {otherError && <ErrorMessage text={otherError} />}
            <NavigationButtons
                onClick={validateFields}
                showNextButton={showNextButton}
                centerOnSmallerWidth={viewType !== ViewType.mobile}
                nextUrl={viewType === ViewType.mobile ? 'belop' : 'nettbank-belop'}
            />
        </>
    );
};

export default NewCrossBorderReceiver;
